import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IdeaCluster } from '../services/fieldUpdateService';

interface Props {
  cluster: IdeaCluster;
  selected: boolean;
  onToggle: (clusterId: string) => void;
}

const CATEGORY_STYLES: Record<string, string> = {
  novel: 'bg-green-50 border-green-200',
  reinforcing: 'bg-slate-50 border-slate-200',
  outlier: 'bg-amber-50 border-amber-200',
};

const IdeaClusterCard: React.FC<Props> = ({ cluster, selected, onToggle }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const cardStyle = CATEGORY_STYLES[cluster.category] ?? 'bg-white border-slate-200';

  return (
    <div className={`border rounded p-3 text-sm ${cardStyle} ${selected ? 'ring-2 ring-blue-400' : ''}`}>
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(cluster.cluster_id)}
          className="mt-0.5 shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => setExpanded(v => !v)}
              className="text-left font-medium text-slate-800 hover:text-blue-700 truncate"
            >
              {cluster.cluster_name}
            </button>
            <span className="text-xs text-slate-400 shrink-0">
              {cluster.doc_count} {t('fieldUpdate.docsLabel', 'docs')}, {cluster.chunk_count} {t('fieldUpdate.chunksLabel', 'chunks')}
            </span>
          </div>

          {!expanded && (
            <p className="text-slate-500 text-xs mt-1 line-clamp-2">{cluster.description}</p>
          )}

          {expanded && (
            <div className="mt-2 space-y-2">
              <p className="text-slate-600 text-xs">{cluster.description}</p>
              {cluster.reinforces_solution && (
                <p className="text-xs text-slate-400 italic">
                  {t('fieldUpdate.reinforces', 'Reinforces: {{solution}}', { solution: cluster.reinforces_solution })}
                </p>
              )}
              {cluster.evidence_quotes.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-slate-500">{t('fieldUpdate.evidence', 'Evidence:')}</p>
                  {cluster.evidence_quotes.map((q, i) => (
                    <blockquote key={i} className="border-l-2 border-slate-300 pl-2 text-xs text-slate-500 italic">
                      &ldquo;{q.quote.slice(0, 200)}&rdquo;
                      <span className="ml-1 text-slate-400 not-italic">— {q.doc_id}{q.page_number != null ? ` §p${q.page_number}` : ''}</span>
                    </blockquote>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IdeaClusterCard;
